import * as React from "react"
import { Download, Filter, Users } from "lucide-react"

import { cn } from "../../lib/utils"
import {
  IconContainer,
  type IconContainerSize,
} from "../../components/blocks/icon-container"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../../components/ui/breadcrumb"
import { Button } from "../../components/ui/button"

/**
 * Get title text size class based on header size
 */
const getTitleSize = (size: IconContainerSize): string => {
  switch (size) {
    case 32:
      return "text-lg font-semibold tracking-tight" // Small
    case 36:
      return "text-xl font-semibold tracking-tight" // Small-medium
    case 44:
      return "text-2xl font-semibold tracking-tight" // Medium (default)
    case 52:
      return "text-3xl font-semibold tracking-tight" // Large
    case 72:
      return "text-4xl font-bold tracking-tight" // Extra large
    case 96:
      return "text-5xl font-bold tracking-tight" // Huge
    default:
      return "text-2xl font-semibold tracking-tight"
  }
}

/**
 * PageHeaderBlock
 * A flexible page header block with title, subtitle, and action buttons.
 * Provides a consistent header layout for application pages.
 */
export interface PageHeaderBlockProps
  extends Readonly<React.HTMLAttributes<HTMLElement>> {
  /** Main page title */
  readonly title?: string
  /** Optional subtitle or description */
  readonly subtitle?: string
  /** Size of the header - affects icon container and title size */
  readonly size?: IconContainerSize
  /** Whether to show action buttons */
  readonly showActions?: boolean
  /** Whether to show breadcrumb navigation */
  readonly showBreadcrumb?: boolean
  /** Breadcrumb items array */
  readonly breadcrumbItems?: Array<{
    label: string
    href?: string
    isCurrentPage?: boolean
  }>
  /** Whether to use product icon instead of regular icon */
  readonly useProductIcon?: boolean
  /** Product icon element to display when useProductIcon is true */
  readonly productIcon?: React.ReactNode
}

export function PageHeader({
  className,
  title = "Page Title",
  subtitle = "Subtitle",
  size = 44,
  showActions = true,
  showBreadcrumb = true,
  breadcrumbItems = [
    { label: "Discover", href: "#" },
    { label: "Chat Inbox", href: "#" },
    { label: "Templates", isCurrentPage: true },
  ],
  useProductIcon = false,
  productIcon,
  ...props
}: PageHeaderBlockProps) {
  return (
    <header
      data-slot="page-header-block"
      className={cn("space-y-4", className)}
      aria-labelledby="page-header-title"
      {...props}
    >
      {showBreadcrumb && breadcrumbItems && breadcrumbItems.length > 0 && (
        <Breadcrumb aria-label="Breadcrumb">
          <BreadcrumbList>
            {breadcrumbItems.map((item, index) => (
              <React.Fragment key={`${item.label}-${index}`}>
                <BreadcrumbItem>
                  {item.isCurrentPage ? (
                    <BreadcrumbPage aria-current="page">
                      {item.label}
                    </BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink href={item.href ?? "#"}>
                      {item.label}
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
                {index < breadcrumbItems.length - 1 && <BreadcrumbSeparator />}
              </React.Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
      )}
      <div className="flex flex-row items-center justify-between">
        <div className="flex gap-4 flex-row items-center">
          <IconContainer
            size={size}
            color="primary"
            useProductIcon={useProductIcon}
            productIcon={productIcon}
            aria-hidden={!!productIcon && useProductIcon ? true : undefined}
          >
            <Users />
          </IconContainer>
          <div>
            <h1 id="page-header-title" className={getTitleSize(size)}>
              {title}
            </h1>
            {subtitle && (
              <p
                className="text-sm text-muted-foreground"
                aria-describedby="page-header-title"
              >
                {subtitle}
              </p>
            )}
          </div>
        </div>
        {showActions && (
          <div className="flex items-center gap-2" aria-label="Header actions">
            <Button variant="ghost" size="icon" aria-label="Download">
              <Download className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" aria-label="Users">
              <Users className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" aria-label="Filter">
              <Filter className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </header>
  )
}

export default PageHeader
